"use client";

// WO-GF-001-P1B-S13 (RW3-6) — the HUD **DIRECTORY** button opens a MENU, it does not jump.
//
// The founder expected a category dropdown behind DIRECTORY instead of a direct jump into the directory.
// The WO said "verify live FIRST". Live agrees, so this is a straight port and the founder override was
// NOT needed (read-only walk, production /mountain-whistler @1440, 2026-07-22):
//   • clicking DIRECTORY leaves the URL UNCHANGED and opens a `MuiMenu-list`;
//   • the menu holds exactly five rows — MOUNTAIN INFO · ALL VENDORS · ALL RENTALS · TRANSPORT OPERATORS ·
//     AIRPORTS — and each was clicked one-per-page-load with its destination URL observed. Those five
//     destinations are in `hub-nav.directoryMenuHref`, and every one of them is a build-served page for
//     every mountain (off-Whistler ALL RENTALS → the served /ski-rentals, the standing F59 precedent).
//
// ONE LAYER, BOTH HUD SURFACES. The DIRECTORY control is an `<a href>` on the 30 landings (SiteHeaderHub)
// and a captured `<button>` wired by HubChromeLayer on the ~361 sub-pages. This layer is document-scoped
// like the RW2-5 change-mountain menu, so it covers both without touching either implementation, and it
// suppresses the underlying navigation the same way live does (the href STAYS in the DOM, so the crawlable
// link graph is unchanged — S7's HUD-nav edge is not removed, only intercepted on click).
//
// PIXEL- AND LINK-GRAPH-NEUTRAL AT REST: nothing is injected until the first open — the menu element is
// created lazily and mounted on <body> with position:fixed (RW2-5's escape from the header's
// overflow:hidden ancestors, which would otherwise clip it). At scroll 0 with the menu closed the served
// DOM is byte-identical to the capture.
//
// Harness hooks: [data-behavior="hud-directory"] (the trigger), [data-hud-directory-menu] (the panel),
// [data-hud-directory-item] (each row).

import { useEffect } from "react";
import { directoryMenuOptions, mountainCodeFromPath } from "./hub-nav";

const WIRED = "data-hud-directory-wired";

export default function HudDirectoryLayer() {
  useEffect(() => {
    const code = mountainCodeFromPath(location.pathname);
    if (!code) return;

    // the HUD's DIRECTORY control on either surface (<a> on landings, <button> on sub-pages).
    const trigger = Array.from(
      document.querySelectorAll<HTMLElement>("header a, header button"),
    ).find(
      (el) =>
        (el.querySelector("p")?.textContent || el.textContent || "")
          .trim()
          .toLowerCase() === "directory",
    );
    if (!trigger || trigger.hasAttribute(WIRED)) return;
    trigger.setAttribute(WIRED, "1");

    const menu = document.createElement("div");
    menu.setAttribute("data-hud-directory-menu", "");
    menu.className = "MuiList-root MuiList-padding MuiMenu-list";
    Object.assign(menu.style, {
      position: "fixed",
      display: "none", // ← nothing renders until the trigger is clicked (pixel-neutral at rest)
      zIndex: "2400",
      minWidth: "200px",
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.15)",
      borderRadius: "6px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      padding: "6px 0",
      overflowY: "auto",
    } satisfies Partial<CSSStyleDeclaration>);

    for (const opt of directoryMenuOptions(code)) {
      const a = document.createElement("a");
      a.setAttribute("data-hud-directory-item", opt.label);
      a.href = opt.href;
      a.textContent = opt.label;
      Object.assign(a.style, {
        display: "block",
        padding: "8px 16px",
        fontSize: "13px",
        letterSpacing: "0.4px",
        color: "rgb(33,37,41)",
        textDecoration: "none",
        whiteSpace: "nowrap",
      } satisfies Partial<CSSStyleDeclaration>);
      menu.appendChild(a);
    }
    document.body.appendChild(menu);

    trigger.setAttribute("data-behavior", "hud-directory");
    trigger.setAttribute("aria-haspopup", "true");
    trigger.setAttribute("aria-expanded", "false");
    trigger.style.cursor = "pointer";

    // anchor under the trigger, recomputed per open so it stays right after the RW-13 HUD shrink/resize.
    const place = () => {
      const r = trigger.getBoundingClientRect();
      menu.style.top = `${Math.round(r.bottom + 4)}px`;
      menu.style.left = `${Math.round(r.left)}px`;
      menu.style.maxHeight = `${Math.min(360, Math.max(160, window.innerHeight - r.bottom - 24))}px`;
    };
    const setOpen = (open: boolean) => {
      if (open) place();
      menu.style.display = open ? "block" : "none";
      trigger.setAttribute("aria-expanded", String(open));
    };
    // capture-phase + stopPropagation so the S7 HUD-nav click handler (and the <a>'s own navigation)
    // never fires — live's DIRECTORY does not navigate, it opens this menu.
    const onTrigger = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(menu.style.display === "none");
    };
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.contains(t) && !trigger.contains(t)) setOpen(false);
    };
    const onReflow = () => {
      if (menu.style.display !== "none") place();
    };

    trigger.addEventListener("click", onTrigger, true);
    document.addEventListener("click", onDocClick);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, { passive: true });

    return () => {
      trigger.removeEventListener("click", onTrigger, true);
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow);
      trigger.removeAttribute(WIRED);
      menu.remove();
    };
  }, []);

  return <span data-hud-directory-layer hidden />;
}
